import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSun, FiMoon, FiZap, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const DemoThemeSelector = ({ onThemeChange, currentTheme }) => {
  const [selectedTheme, setSelectedTheme] = useState(currentTheme || 'dark-pro');

  const themes = [
    {
      id: 'minimal-light',
      name: 'Minimal Light',
      description: 'Clean and professional light theme',
      icon: FiSun,
      preview: { bg: 'bg-white', text: 'text-gray-900', accent: 'bg-indigo-600', border: 'border-gray-200' }
    },
    {
      id: 'dark-pro',
      name: 'Dark Pro',
      description: 'Sleek dark theme for professionals',
      icon: FiMoon,
      preview: { bg: 'bg-dark-950', text: 'text-white', accent: 'bg-lime-500', border: 'border-dark-700' }
    },
    {
      id: 'cyber-neon',
      name: 'Cyber Neon',
      description: 'Futuristic neon green theme',
      icon: FiZap,
      preview: { bg: 'bg-black', text: 'text-lime-400', accent: 'bg-cyan-400', border: 'border-lime-500' }
    },
  ];

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    if (onThemeChange) {
      onThemeChange(themeId);
    }
  };

  const ThemePreview = ({ theme }) => (
    <div className={`p-4 rounded-lg border-2 ${theme.preview.border} ${theme.preview.bg} transition-all duration-200`}>
      {/* Mock profile header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className={`w-12 h-12 ${theme.preview.accent} rounded-full`}></div>
        <div>
          <div className={`h-3 w-20 ${theme.preview.text} bg-current opacity-80 rounded mb-1`}></div>
          <div className={`h-2 w-16 ${theme.preview.text} bg-current opacity-50 rounded`}></div>
        </div>
      </div>
      {/* Mock content blocks */}
      <div className="space-y-2">
        <div className={`h-8 ${theme.preview.border} border rounded`}></div>
        <div className={`h-6 ${theme.preview.border} border rounded`}></div>
        <div className={`h-10 ${theme.preview.border} border rounded`}></div>
      </div>
    </div>
  );

  return (
    <div className="glass-dark rounded-xl border border-dark-700 p-6 mb-8">
      <h3 className="text-lg font-semibold text-lime-bright mb-4 flex items-center">
        <SafeIcon icon={FiZap} className="mr-2" />
        Try Different Themes
      </h3>
      <p className="text-dark-300 mb-6 text-sm">
        Click on a theme below to see how it looks on this demo profile
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {themes.map((theme) => (
          <motion.div
            key={theme.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 ${
              selectedTheme === theme.id
                ? 'border-lime-500 ring-2 ring-lime-500/30 shadow-glow-lime-sm'
                : 'border-dark-700 hover:border-lime-500/50'
            }`}
            onClick={() => handleThemeSelect(theme.id)}
          >
            {/* Selection indicator */}
            {selectedTheme === theme.id && (
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-lime-500 text-dark-950 rounded-full flex items-center justify-center text-sm shadow-glow-lime-sm z-10">
                <SafeIcon icon={FiCheck} />
              </div>
            )}
            {/* Theme preview */}
            <ThemePreview theme={theme} />
            {/* Theme info */}
            <div className="p-4 border-t border-dark-700">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={theme.icon} className="text-lime-400" />
                <h4 className="font-medium text-white">{theme.name}</h4>
              </div>
              <p className="text-sm text-dark-300">{theme.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default DemoThemeSelector;

